<template>
  <div class="details-content" data-testid="about-details">
    <Heading class="details-content__title" :level="1"
      >Sobre a carteira</Heading
    >
    <Grid :columns="showNColumns" gutter="xm">
      <ListDisplay
        v-if="portfolioDistribution?.portfolio?.performanceFixedRate"
        title="Meta de retorno"
        :align-vertical="false"
        has-divider
      >
        <template #value-slot>
          <div class="details-content__value">
            <BaseText size="md" :color="NebraskaColors.elementPrimary">{{
              portfolioDistribution?.portfolio?.performanceFixedRate
            }}</BaseText>
            <ButtonIconContextual
              icon="EF0080"
              label="Meta de retorno"
              size="small"
              data-testid="performancefixedrate-open-modal"
              @keydown.space.enter="
                openMandateDetail(MandateType.PerformanceFixedRate)
              "
              @click="openMandateDetail(MandateType.PerformanceFixedRate)"
            />
          </div>
        </template>
      </ListDisplay>
      <ListDisplay
        v-if="portfolioDistribution?.portfolio?.minimumDeposit"
        class="details-content__row"
        title="Investimento mínimo"
        :value="getMoneyFormat(portfolioDistribution.portfolio?.minimumDeposit)"
        :align-vertical="false"
        has-divider
      />
      <ListDisplay
        v-if="portfolioDistribution?.portfolio?.performanceFixedRate"
        title="Meta de volatilidade"
        :align-vertical="false"
        has-divider
      >
        <template #value-slot>
          <div class="details-content__value">
            <BaseText size="md" :color="NebraskaColors.elementPrimary">{{
              portfolioDistribution?.portfolio?.volatility
            }}</BaseText>
            <ButtonIconContextual
              icon="EF0080"
              label="Detalhes"
              size="small"
              data-testid="volatility-open-modal"
              @keydown.space.enter="openMandateDetail(MandateType.Volatility)"
              @click="openMandateDetail(MandateType.Volatility)"
            />
          </div>
        </template>
      </ListDisplay>
      <ListDisplay
        v-if="portfolioDistribution?.portfolio?.minimumDeposit"
        class="details-content__row"
        title="Resgate mínimo"
        :value="
          getMoneyFormat(portfolioDistribution.portfolio?.minimumRedemption)
        "
        :align-vertical="false"
        has-divider
      />
      <ListDisplay
        v-if="portfolioDistribution?.portfolio?.maximumDrawdown"
        title="Meta de máximo drawdown"
        :align-vertical="false"
        has-divider
      >
        <template #value-slot>
          <div class="details-content__value">
            <BaseText size="md" :color="NebraskaColors.elementPrimary">{{
              portfolioDistribution?.portfolio?.maximumDrawdown
            }}</BaseText>
            <ButtonIconContextual
              icon="EF0080"
              label="Detalhes"
              size="small"
              data-testid="maximumdrawdown-open-modal"
              @keydown.space.enter="
                openMandateDetail(MandateType.MaximumDrawdown)
              "
              @click="openMandateDetail(MandateType.MaximumDrawdown)"
            />
          </div>
        </template>
      </ListDisplay>
      <ListDisplay
        v-if="portfolioDistribution?.portfolio?.goalDuration"
        title="Maturação do retorno"
        :align-vertical="false"
        has-divider
      >
        <template #value-slot>
          <div class="details-content__value">
            <BaseText size="md" :color="NebraskaColors.elementPrimary">{{
              portfolioDistribution?.portfolio?.goalDuration
            }}</BaseText>
            <ButtonIconContextual
              icon="EF0080"
              label="Detalhes"
              size="small"
              data-testid="goalduration-open-modal"
              @keydown.space.enter="openMandateDetail(MandateType.GoalDuration)"
              @click="openMandateDetail(MandateType.GoalDuration)"
            />
          </div>
        </template>
      </ListDisplay>
      <ListDisplay
        v-if="portfolioDistribution?.portfolio?.yearlyAliquotFormatted"
        title="Custos"
        :align-vertical="false"
        has-divider
      >
        <template #value-slot>
          <div class="details-content__value">
            <BaseText size="md" :color="NebraskaColors.elementPrimary">{{
              portfolioDistribution?.portfolio?.yearlyAliquotFormatted
            }}</BaseText>
            <ButtonIconContextual
              icon="EF0080"
              label="Detalhes"
              size="small"
              data-testid="costs-open-modal"
              @keydown.space.enter="openCostsDetail()"
              @click="openCostsDetail()"
            />
          </div>
        </template>
      </ListDisplay>
    </Grid>
    <div v-if="shouldShowDisclaimer" class="details-content__disclaimer">
      <BaseText
        class="details-content__disclaimer__text"
        size="sm"
        :color="NebraskaColors.elementSecondary"
      >
        As metas atreladas às carteiras administradas são projeções e, por isso,
        não se tratam de promessas ou garantia de retornos futuros. Para mais
        informações, acesse a Política de Investimentos.
      </BaseText>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, provide, ref } from 'vue';
import {
  BaseText,
  Heading,
  Grid,
  ListDisplay,
  ButtonIconContextual
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { MandateType } from '@/modules/wealth/types/mandate-types';
import { moneyFormat } from '@/modules/common/helpers/currency';
import { IPortfolioDistributionResponse } from '@/modules/wallet-builder/src/services/types/models/IBffInvestments';
import { DetailType, IDetailModalProps } from '@/modules/wealth/types';

const emit = defineEmits(['openDetail']);

const portfolioDistribution = ref<IPortfolioDistributionResponse | undefined>(
  inject<IPortfolioDistributionResponse>('portfolioDistribution')
);

const detailModalProps = ref<IDetailModalProps>({
  title: '',
  type: ''
});

const shouldShowDisclaimer = computed(() => {
  return (
    portfolioDistribution.value?.portfolio?.riskCategory &&
    portfolioDistribution.value?.portfolio?.goalDuration &&
    portfolioDistribution.value?.portfolio?.performanceFixedRate &&
    portfolioDistribution.value?.portfolio?.volatility &&
    portfolioDistribution.value?.portfolio?.maximumDrawdown
  );
});

const showNColumns = computed(() => {
  const columnsCount = [
    portfolioDistribution.value?.portfolio?.minimumDeposit,
    portfolioDistribution.value?.portfolio?.performanceFixedRate,
    portfolioDistribution.value?.portfolio?.minimumRedemption,
    portfolioDistribution.value?.portfolio?.maximumDrawdown,
    portfolioDistribution.value?.portfolio?.goalDuration,
    portfolioDistribution.value?.portfolio?.yearlyAliquotFormatted,
    portfolioDistribution.value?.portfolio?.performanceFixedRate
  ].filter(item => item).length;
  return columnsCount > 5 ? [1, 1] : [1];
});

function openCostsDetail() {
  detailModalProps.value.title =
    portfolioDistribution?.value?.portfolio?.portfolioSummaryDetails.costs
      ?.title ?? '';
  detailModalProps.value.details = {
    description:
      portfolioDistribution?.value?.portfolio?.portfolioSummaryDetails.costs
        ?.description
  };
  detailModalProps.value.type = DetailType.Cost;

  emit('openDetail', detailModalProps.value);
}

function openMandateDetail(type: MandateType) {
  switch (type) {
    case MandateType.RiskCategory:
      detailModalProps.value.title =
        portfolioDistribution?.value?.portfolio?.portfolioSummaryDetails
          .riskCategory.title ?? '';
      detailModalProps.value.details = {
        description:
          portfolioDistribution?.value?.portfolio?.portfolioSummaryDetails
            .riskCategory?.description
      };
      break;
    case MandateType.PerformanceFixedRate:
      detailModalProps.value.title =
        portfolioDistribution?.value?.portfolio?.portfolioSummaryDetails
          .performanceFixedRate.title ?? '';
      detailModalProps.value.details = {
        description:
          portfolioDistribution?.value?.portfolio?.portfolioSummaryDetails
            .performanceFixedRate?.description
      };
      break;
    case MandateType.GoalDuration:
      detailModalProps.value.title =
        portfolioDistribution?.value?.portfolio?.portfolioSummaryDetails
          .goalDuration.title ?? '';
      detailModalProps.value.details = {
        description:
          portfolioDistribution?.value?.portfolio?.portfolioSummaryDetails
            .goalDuration?.description
      };
      break;
    case MandateType.Volatility:
      detailModalProps.value.title =
        portfolioDistribution?.value?.portfolio?.portfolioSummaryDetails
          .volatility.title ?? '';
      detailModalProps.value.details = {
        description:
          portfolioDistribution?.value?.portfolio?.portfolioSummaryDetails
            .volatility?.description
      };
      break;
    case MandateType.MaximumDrawdown:
      detailModalProps.value.title =
        portfolioDistribution?.value?.portfolio?.portfolioSummaryDetails
          .drawdown.title ?? '';
      detailModalProps.value.details = {
        description:
          portfolioDistribution?.value?.portfolio?.portfolioSummaryDetails
            .drawdown?.description
      };
      break;
  }

  detailModalProps.value.type = DetailType.Mandate;
  emit('openDetail', detailModalProps.value);
}

function getMoneyFormat(value: number) {
  return moneyFormat(value, false);
}

provide('detailModalProps', detailModalProps);
</script>

<style lang="less" scoped>
.details-content__subtext {
  color: @text-secondary;
}

.details-content__title {
  margin-bottom: @size-spacing-x600;
}

.details-content__placeholder {
  float: left;
  background: @element-secondary;
  border-radius: 4px;
  width: 100%;
  height: @size-spacing-x500;
  display: block;
  white-space: nowrap;
}
.details-content__value {
  display: flex;
  align-items: center;
  justify-content: center;
  color: @element-primary;
  gap: @size-spacing-x150;
}
.details-content__top-right {
  vertical-align: middle;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.details-content__top-left {
  white-space: nowrap;
}
span.details-content__icon-horizontal {
  display: flex;
  align-items: center;
}
.details-content__icon-horizontal {
  :deep(.svg-icon) {
    display: flex;
    align-items: center;
    margin-left: 10px;
  }
}
.details-content__row {
  display: flex;
  :deep(.icon) {
    display: none;
  }
}

.details-content__disclaimer {
  margin-top: @size-spacing-x500;
  background: @background-primary;
  padding: @size-spacing-x500;
  border-radius: @size-radius-x200;
}

:deep(.list-display__content) {
  width: 100%;
}

:deep(.list-display__content .list-display__slot-title .list-display__text) {
  color: @element-primary !important;
}

:deep(.list-display__divider) {
  padding-top: @size-spacing-x350;
}

:deep(.list-display__divider:last-of-type),
:deep(.list-display__divider:nth-last-of-type(2)) {
  border-bottom: none;
}

:deep(.button-icon .content) {
  justify-content: flex-end;
}

:deep(.button--label) {
  width: fit-content;
}
</style>
